<template>
  <v-dialog v-model="dialog" persistent scrollable max-width="800">
    <v-card v-if="selectedItem">
      <v-card-title class="title">Order #{{ selectedItem.id }}</v-card-title>
      <v-card-text style="height: 300px;">
        <v-layout row wrap>
          <v-flex xs3>
            <div class="body-2">Title</div>
          </v-flex>
          <v-flex xs9>
            <div class="body-1">{{ selectedItem.title }}</div>
          </v-flex>
        </v-layout>

        <v-layout row wrap>
          <v-flex xs3>
            <div class="body-2">Merchant Order ID</div>
          </v-flex>
          <v-flex xs9>
            <div class="body-1">{{ selectedItem.order_id }}</div>
          </v-flex>
        </v-layout>

        <v-layout row wrap>
          <v-flex xs3>
            <div class="body-2">Price</div>
          </v-flex>
          <v-flex xs9>
            <div class="body-1">
              {{
                selectedItem.price_amount + " " + selectedItem.price_currency
              }}
            </div>
          </v-flex>
        </v-layout>

        <v-layout row wrap>
          <v-flex xs3>
            <div class="body-2">Pay amount</div>
          </v-flex>
          <v-flex xs9>
            <div class="body-1">
              {{
                selectedItem.pay_currency
                    ? selectedItem.pay_amount + " " + selectedItem.pay_currency
                    : ""
              }}
              <v-chip
                  v-if="selectedItem.network"
                  label
                  small
                  outlined
                  :color="getColorByNetwork(selectedItem.network)"
              >
                {{
                  selectedItem.network === "trx"
                      ? "TRC20"
                      : selectedItem.network === "eth"
                          ? "ERC20"
                          : ""
                }}
              </v-chip>
            </div>
          </v-flex>
        </v-layout>

        <v-layout row wrap>
          <v-flex xs3>
            <div class="body-2">{{ $t("common.status") }}</div>
          </v-flex>
          <v-flex xs9>
            <v-chip
              outlined
              label
              small
              :color="getColorByStatus(selectedItem.status)"
              class="mr-2"
              >{{ selectedItem.status | uppercase }}</v-chip
            >
            <v-btn
              color="blue"
              outlined
              small
              @click="checkBalance"
              class="mr-2"
              >Check balance</v-btn
            >
            <v-btn
              color="error"
              outlined
              small
              class="mr-2"
              @click="markPaid"
              v-if="showMarkPaid()"
              >Mark as paid</v-btn
            >
          </v-flex>
        </v-layout>

        <v-layout row wrap>
          <v-flex xs3>
            <div class="body-2">{{ $t("common.createdAt") }}</div>
          </v-flex>
          <v-flex xs9>
            <div class="body-1">
              {{ showDateTime(selectedItem, "created_at") }}
            </div>
          </v-flex>

          <v-flex xs3>
            <div class="body-2">{{ $t("common.updatedAt") }}</div>
          </v-flex>
          <v-flex xs9>
            <div class="body-1">
              {{ showDateTime(selectedItem, "updated_at") }}
            </div>
          </v-flex>
        </v-layout>

        <v-layout row wrap>
          <v-flex xs3>
            <div class="body-2">Description</div>
          </v-flex>
          <v-flex xs9>
            <div class="body-1">
              {{ selectedItem.description }}
            </div>
          </v-flex>

          <v-flex xs3>
            <div class="body-2">App</div>
          </v-flex>
          <v-flex xs9>
            <div class="body-1">
              {{ selectedItem.app_title }}
            </div>
          </v-flex>
        </v-layout>

        <v-layout row wrap>
          <v-flex xs3>
            <div class="body-2">Received wallet</div>
          </v-flex>
          <v-flex xs9>
            <div class="body-1" v-if="selectedItem.wallet_address">
              <a
                :href="
                  verifyAddress(
                    selectedItem.wallet_address,
                    selectedItem.pay_currency,
                    selectedItem.network
                  )
                "
                target="_blank"
                >{{ selectedItem.wallet_address }}
              </a>
            </div>
            <div class="body-1" v-else-if="selectedItem.tracking_address">
              <a
                :href="
                  verifyAddress(
                    selectedItem.tracking_address,
                    selectedItem.pay_currency,
                    selectedItem.network
                  )
                "
                target="_blank"
                >{{ selectedItem.tracking_address }}
              </a>
            </div>
          </v-flex>

          <v-flex xs3>
            <div class="body-2">Received amount</div>
          </v-flex>
          <v-flex xs9>
            <div class="body-1">
              {{
                selectedItem.received_amount +
                  (selectedItem.pay_currency
                    ? " " + selectedItem.pay_currency
                    : "")
              }}
            </div>
          </v-flex>
        </v-layout>

        <v-data-table
          :headers="headerTransactions"
          :items="selectedItem.transactions"
          item-key="id"
          hide-default-footer
          :mobile-breakpoint="0"
        >
          <template v-slot:item.hash="{ item }">
            <a
                :href="verifyTransaction(item.hash,
                      selectedItem.pay_currency,
                      selectedItem.network)"
                target="_blank"
            >{{ item.hash }}</a
            >
          </template>

          <template v-slot:item.amount="{ item }">
            {{ showCryptoAmount(item.amount, selectedItem.pay_currency) }}
          </template>
          <template v-slot:item.confirmations="{ item }">
            {{ showConfirmation(item.confirmations) }}
          </template>
          <template v-slot:item.created_at="{ item }">
            {{ showDateTime(item, "created_at") }}
          </template>
        </v-data-table>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="blue darken-1" text @click="close">{{
          $t("button.close")
        }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  verifyAddress,
  verifyTransaction,
  showCryptoAmount,
  getColorByNetwork
} from "@/util/cryptoCurrencies";
import Util from "@/util";

export default {
  name: "OrderDialog",
  data() {
    return {
      headerTransactions: [
        {
          text: "Transaction hash",
          value: "hash",
          sortable: false,
          width: "220"
        },
        { text: "Amount", value: "amount", align: "end", width: "150" },
        {
          text: "Confirmations",
          value: "confirmations",
          align: "end",
          width: "120"
        },
        { text: "", value: "created_at", width: "150" }
      ]
    };
  },
  props: {
    selectedItem: Object,
    dialog: Boolean,
    showDateTime: Function,
    checkBalance: Function,
    markPaid: Function,
    close: Function
  },
  methods: {
    getColorByNetwork,
    verifyAddress,
    verifyTransaction,
    showCryptoAmount,
    getColorByStatus(status) {
      return Util.getColorByStatus(status);
    },
    showMarkPaid() {
      let status = this.selectedItem.status;
      return (
        status === "pending" ||
        status === "expired" ||
        status === "illegal" ||
        status === "fraud"
      );
    },
    showConfirmation(number) {
      if (number > 6) {
        return "5+";
      } else {
        return number;
      }
    }
  }
};
</script>

<style scoped></style>
